import { listSupportedLocales, toBcp47 } from '../../domain/i18n/locales';

/**
 * Keep the document head honest about which language is on screen and which
 * page it is showing.
 *
 * Five things move together on every locale or route change, and all five are
 * SEO- or accessibility-load-bearing:
 *   - `<html lang>`, so assistive tech and the browser pick the right voice and
 *     hyphenation,
 *   - `<title>` and `<meta name="description">`, because a single-page app that
 *     never rewrites them ships one title for twelve cars, and that title is
 *     what a bookmark, a tab strip and a search result all show,
 *   - `<link rel="canonical">`, so the mirrors below do not compete with the
 *     real domain for the same content,
 *   - one `<link rel="alternate" hreflang>` per shipped locale plus `x-default`,
 *     so a search engine can offer the right one.
 *
 * Managed imperatively rather than in JSX because these tags live in `<head>`,
 * outside the React root. The COPY is not this module's: the title and the
 * description arrive already resolved through `t()`, because deciding which
 * message key a route uses is the screen's business and putting a route table of
 * keys in here would be a second one to keep in step with `app/routes.js`.
 */

const MANAGED_ATTRIBUTE = 'data-legends-locale-link';

/**
 * The one public origin. Firebase serves the same content on the custom domain
 * AND on two firebase-owned mirrors, so without this every mirror would
 * self-canonicalise and compete with the real domain for the same content — the
 * exact duplicate-content split the hreflang graph exists to prevent.
 *
 * Anything not in this set (localhost, a preview channel) canonicalises to
 * itself, so a preview never hands out an address claiming to be production.
 */
export const PRODUCTION_ORIGIN = 'https://legends.habb-me.com';

const PRODUCTION_HOSTNAMES = new Set([
    'legends.habb-me.com',
    'legends-habb-me.web.app',
    'legends-habb-me.firebaseapp.com',
]);

export function resolveCanonicalOrigin(locationObject) {
    if (!locationObject) {
        return PRODUCTION_ORIGIN;
    }

    return PRODUCTION_HOSTNAMES.has(locationObject.hostname)
        ? PRODUCTION_ORIGIN
        : locationObject.origin;
}

/**
 * The page's name, then the product's.
 *
 * One separator, decided here, so twelve screens do not each pick their own. The
 * splash passes no title at all: its page IS the brand, and "LEGENDS — LEGENDS"
 * is what a per-screen composition produces when nobody owns the join.
 */
export function formatDocumentTitle(title, brandName) {
    const pageTitle = String(title ?? '').trim();

    return pageTitle && pageTitle !== brandName ? `${pageTitle} — ${brandName}` : String(brandName ?? '');
}

function normalizeRoute(routePath) {
    return routePath === '/' ? '' : String(routePath || '').replace(/\/$/u, '');
}

function upsertLink(documentObject, rel, href, hreflang) {
    const selector = hreflang
        ? `link[${MANAGED_ATTRIBUTE}][rel="${rel}"][hreflang="${hreflang}"]`
        : `link[${MANAGED_ATTRIBUTE}][rel="${rel}"]`;
    const existingLink = documentObject.head.querySelector(selector);
    const link = existingLink || documentObject.createElement('link');

    link.setAttribute(MANAGED_ATTRIBUTE, '');
    link.setAttribute('rel', rel);
    link.setAttribute('href', href);

    if (hreflang) {
        link.setAttribute('hreflang', hreflang);
    }

    if (!existingLink) {
        documentObject.head.appendChild(link);
    }
}

function upsertMeta(documentObject, name, content) {
    const existingMeta = documentObject.head.querySelector(`meta[name="${name}"]`);
    const meta = existingMeta || documentObject.createElement('meta');

    meta.setAttribute('name', name);
    meta.setAttribute('content', content);

    if (!existingMeta) {
        documentObject.head.appendChild(meta);
    }
}

export function buildAlternateLinks(routePath, origin) {
    const normalizedRoute = normalizeRoute(routePath);

    return listSupportedLocales().map((localeCode) => ({
        hreflang: toBcp47(localeCode),
        href: `${origin}/${localeCode}${normalizedRoute}`,
    }));
}

export function applyLocaleDocumentMetadata({
    localeCode,
    routePath = '/',
    title = null,
    description = null,
    documentObject = typeof document === 'undefined' ? null : document,
    origin = resolveCanonicalOrigin(typeof window === 'undefined' ? null : window.location),
}) {
    if (!documentObject || !localeCode) {
        return;
    }

    const bcp47 = toBcp47(localeCode);

    if (!bcp47) {
        return;
    }

    documentObject.documentElement.setAttribute('lang', bcp47);

    // A blank title or description is worse than a stale one: the previous
    // screen's is at least about this product, and an empty `<title>` shows the
    // URL in the tab strip. So each is only written when the caller has one.
    if (title) {
        documentObject.title = title;
    }

    if (description) {
        upsertMeta(documentObject, 'description', description);
    }

    const normalizedRoute = normalizeRoute(routePath);

    upsertLink(documentObject, 'canonical', `${origin}/${localeCode}${normalizedRoute}`);

    buildAlternateLinks(routePath, origin).forEach(({ hreflang, href }) => {
        upsertLink(documentObject, 'alternate', href, hreflang);
    });

    // x-default points at the unprefixed root, which negotiates per visitor —
    // the correct target for "we have no page specifically for your locale".
    upsertLink(documentObject, 'alternate', `${origin}/`, 'x-default');
}
